import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { GoogleDocsExportResponse, GoogleDocsExportStatus } from '@/types/google-docs';
import { toError } from '@/utils/errors';
import { usePoll } from './usePoll';

const DEFAULT_POLL_INTERVAL = 3000; // 3 seconds
const MAX_POLL_DURATION = 120000; // 2 minutes

/**
 * State of the Google Docs export operation
 */
export type GoogleDocsExportState =
  | 'idle'
  | 'checking'
  | 'triggering'
  | 'polling'
  | 'completed'
  | 'failed'
  | 'not_configured';

interface UseGoogleDocsExportOptions {
  /**
   * Polling interval in milliseconds
   * @default 3000
   */
  pollInterval?: number;
  /**
   * Callback when export completes successfully
   */
  onSuccess?: (googleDocUrl: string) => void;
  /**
   * Callback when export fails
   */
  onError?: (error: Error) => void;
  /**
   * Create an export on mount when none exists yet. Off by default:
   * opening a report must not write — generating is an explicit action
   * (`triggerExport`). An export that already exists is always looked up
   * and, if still running, followed.
   * @default false
   */
  autoTrigger?: boolean;
}

interface UseGoogleDocsExportReturn {
  /**
   * Trigger the export manually
   */
  triggerExport: () => Promise<void>;
  /**
   * Retry a failed export
   */
  retryExport: () => Promise<void>;
  /**
   * Current state of the export operation
   */
  state: GoogleDocsExportState;
  /**
   * Current status from the API
   */
  status: GoogleDocsExportStatus | null;
  /**
   * Google Doc URL (if export completed)
   */
  googleDocUrl: string | null;
  /**
   * Document title (if export completed)
   */
  documentTitle: string | null;
  /**
   * Error message (if failed)
   */
  error: string | null;
  /**
   * Export ID (for retry operations)
   */
  exportId: string | null;
  /**
   * Whether the export is in progress
   */
  isLoading: boolean;
}

const isRunning = (status: GoogleDocsExportStatus) =>
  status === 'pending' || status === 'exporting';

/**
 * Custom hook for Google Docs export functionality
 *
 * Looks up the export for a completed blended report when one is viewed,
 * follows a running export to completion and exposes the Google Doc link
 * when ready. Creating an export is a user action (`triggerExport`) unless
 * `autoTrigger` is set.
 *
 * @example
 * ```tsx
 * const { state, googleDocUrl, error, retryExport } = useGoogleDocsExport(blendedReportId, {
 *   onSuccess: (url) => console.log('Export complete!', url),
 *   onError: (error) => console.error('Export failed:', error),
 * });
 *
 * // Display result
 * {state === 'completed' && googleDocUrl && (
 *   <Anchor href={googleDocUrl} target="_blank">View in Google Docs</Anchor>
 * )}
 * ```
 */
export function useGoogleDocsExport(
  blendedReportId: string | null,
  options: UseGoogleDocsExportOptions = {}
): UseGoogleDocsExportReturn {
  const { pollInterval = DEFAULT_POLL_INTERVAL, onSuccess, onError, autoTrigger = false } = options;

  const [state, setState] = useState<GoogleDocsExportState>('idle');
  const [status, setStatus] = useState<GoogleDocsExportStatus | null>(null);
  const [googleDocUrl, setGoogleDocUrl] = useState<string | null>(null);
  const [documentTitle, setDocumentTitle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exportId, setExportId] = useState<string | null>(null);
  /** The export whose status is being polled; null = not polling. */
  const [pollTarget, setPollTarget] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  const processedReportIdRef = useRef<string | null>(null);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  const fail = useCallback((message: string) => {
    setError(message);
    setState('failed');
    setPollTarget(null);
    const err = new Error(message);
    onErrorRef.current?.(err);
    Sentry.captureException(err);
  }, []);

  // Handle export response
  const handleExportResponse = useCallback(
    (response: GoogleDocsExportResponse) => {
      if (!isMountedRef.current) {
        return;
      }

      setStatus(response.status);
      setExportId(response.id);

      if (response.status === 'completed' && response.google_doc_url) {
        setGoogleDocUrl(response.google_doc_url);
        setDocumentTitle(response.document_title);
        setState('completed');
        setPollTarget(null);
        onSuccessRef.current?.(response.google_doc_url);
      } else if (response.status === 'failed') {
        fail(response.error || 'Export failed');
      }
    },
    [fail]
  );

  usePoll<GoogleDocsExportResponse>({
    enabled: pollTarget !== null,
    interval: pollInterval,
    // The export was just kicked off — the first check waits one interval.
    immediate: false,
    maxDurationMs: MAX_POLL_DURATION,
    refetchDeps: [pollTarget],
    queryFn: ({ signal }) => {
      if (!pollTarget) {
        throw new Error('no export to poll');
      }
      return vnextApiClient.getGoogleDocsExportStatus(pollTarget, { signal });
    },
    until: (response) => !isRunning(response.status),
    onData: handleExportResponse,
    // A failed tick is not a failed export: back off and try again.
    onError: (err) => Sentry.captureException(toError(err)),
    onStop: (reason) => {
      if (reason === 'maxDuration') {
        fail('Export operation timed out');
      }
    },
  });

  // Start polling for export status
  const startPolling = useCallback((currentExportId: string) => {
    setState('polling');
    setExportId(currentExportId);
    setPollTarget(currentExportId);
  }, []);

  // Trigger export
  const triggerExport = useCallback(async () => {
    if (!blendedReportId) {
      return;
    }

    try {
      setState('triggering');
      setError(null);

      const response = await vnextApiClient.triggerGoogleDocsExport(blendedReportId);

      if (!isMountedRef.current) {
        return;
      }

      setExportId(response.export_id);

      if (response.status === 'completed') {
        // Export already exists and is complete - fetch full details
        const exportResponse = await vnextApiClient.getGoogleDocsExportStatus(response.export_id);
        handleExportResponse(exportResponse);
      } else if (response.status === 'failed') {
        setError(response.message);
        setState('failed');
      } else {
        // Export in progress - start polling
        startPolling(response.export_id);
      }
    } catch (err) {
      if (!isMountedRef.current) {
        return;
      }

      // Check for 503 - configuration error
      if (err instanceof Error && err.message.includes('503')) {
        setState('not_configured');
        setError('Google Docs integration is not configured');
        return;
      }

      const error = toError(err, 'Failed to trigger export');
      setError(error.message);
      setState('failed');
      onErrorRef.current?.(error);
      Sentry.captureException(error);
    }
  }, [blendedReportId, handleExportResponse, startPolling]);

  // Retry failed export
  const retryExport = useCallback(async () => {
    if (!exportId) {
      return;
    }

    try {
      setState('triggering');
      setError(null);

      const response = await vnextApiClient.retryGoogleDocsExport(exportId);

      if (!isMountedRef.current) {
        return;
      }

      if (response.status === 'completed') {
        const exportResponse = await vnextApiClient.getGoogleDocsExportStatus(response.export_id);
        handleExportResponse(exportResponse);
      } else if (response.status === 'failed') {
        setError(response.message);
        setState('failed');
      } else {
        startPolling(response.export_id);
      }
    } catch (err) {
      if (!isMountedRef.current) {
        return;
      }

      const error = toError(err, 'Failed to retry export');
      setError(error.message);
      setState('failed');
      onErrorRef.current?.(error);
      Sentry.captureException(error);
    }
  }, [exportId, handleExportResponse, startPolling]);

  // Look up the existing export when blendedReportId changes (a read);
  // create one only when asked to.
  useEffect(() => {
    if (!blendedReportId) {
      return;
    }

    // Prevent duplicate lookups for the same report
    if (processedReportIdRef.current === blendedReportId) {
      return;
    }

    const checkExistingExport = async () => {
      try {
        setState('checking');
        processedReportIdRef.current = blendedReportId;

        const existingExport =
          await vnextApiClient.getGoogleDocsExportByBlendedReport(blendedReportId);

        if (!isMountedRef.current) {
          return;
        }

        if (existingExport) {
          handleExportResponse(existingExport);

          // If pending/exporting, follow it
          if (isRunning(existingExport.status)) {
            startPolling(existingExport.id);
          }
        } else if (autoTrigger) {
          triggerExport();
        } else {
          setState('idle'); // nothing yet — generating is the user's call
        }
      } catch (err) {
        if (!isMountedRef.current) {
          return;
        }

        Sentry.captureException(err);

        if (autoTrigger) {
          // If error checking, try to trigger export anyway
          triggerExport();
        } else {
          setState('idle');
        }
      }
    };

    checkExistingExport();
  }, [blendedReportId, autoTrigger, handleExportResponse, startPolling, triggerExport]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset when blendedReportId changes
  useEffect(() => {
    if (!blendedReportId) {
      setState('idle');
      setStatus(null);
      setGoogleDocUrl(null);
      setDocumentTitle(null);
      setError(null);
      setExportId(null);
      setPollTarget(null);
      processedReportIdRef.current = null;
    }
  }, [blendedReportId]);

  return {
    triggerExport,
    retryExport,
    state,
    status,
    googleDocUrl,
    documentTitle,
    error,
    exportId,
    isLoading: state === 'checking' || state === 'triggering' || state === 'polling',
  };
}
